import React from 'react'

import i18n from 'i18n-literally'
import PropTypes from 'prop-types'
import { useConnect } from 'redux-bundler-hook'

import {
  AccountCircleRounded as AccountCircleRoundedIcon,
  BusinessRounded as BusinessRoundedIcon,
  PowerSettingsNewRounded as PowerSettingsNewRoundedIcon,
} from '@mui/icons-material'
import {
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material'
import { makeStyles } from '@mui/styles'

import { Allowed } from '~/src/Flags'
import safeStorage from '~/src/Lib/safeStorage'
import { defer, EMPTY_OBJECT, useMatchesBreakpoint } from '~/src/Lib/Utils'
import Button from '~/src/UI/Shared/Button'
import ConfirmationDialog from '~/src/UI/Shared/ConfirmationDialog'
import TooltipComponent from '~/src/UI/Shared/Tooltip'
import T from '~/src/UI/Shared/Typography'
import PersonWidget from '~/src/UI/Widgets/Person'

const displayName = 'AccountButton'
const storage = safeStorage()

const useStyles = makeStyles(theme => ({
  button: {
    backgroundColor: theme.palette.appPrimary.enabled,
    [theme.breakpoints.down('md')]: {
      backgroundColor: 'unset'
    },
    color: theme.palette.primary.main,
    '@media print': {
      display: 'none'
    },
    '&:hover': {
      color: theme.palette.contrast.strong,
    }
  },
  selected: {
    backgroundColor: theme.palette.appPrimary.active,
    [theme.breakpoints.down('md')]: {
      backgroundColor: 'unset'
    },
    '@media print': {
      display: 'none'
    }
  },
  content: {
    margin: theme.spacing(0.2, 0),
    '@media print': {
      display: 'none'
    }
  },
  menuItem: {
    '& .MuiListItemText-root .MuiListItemText-primary': {
      color: theme.palette.text.primary
    },
  },
}), { name: displayName })

const primaryTypographyProps = { color: 'textPrimary' }

const AccountButtonComponent = ({
  doLogout,
  currentFacility = EMPTY_OBJECT,
  haveHarvestingUnsavedStates,
  me,
  urlObject,
  ...props
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [loggingOut, setLoggingOut] = React.useState(false)
  const handleMenuOpen = e => {
    setAnchorEl(e.target.closest('button'))
  }
  const shouldShowLogoutConfirmation = Boolean(
    haveHarvestingUnsavedStates
    || storage.getItem('testHarvestingUnsavedStates')
  )

  const handleLogout = () => {
    if (shouldShowLogoutConfirmation) {
      setLoggingOut(true)
      return
    }
    doLogout()
  }
  const handleMenuClose = () => {
    defer(() => {
      anchorEl.blur()
    }, defer.priorities.highest)
    setAnchorEl(null)
  }

  const open = Boolean(anchorEl)
  const id = open ? 'me-menu' : undefined

  const classes = useStyles(props)
  const isSmDown = useMatchesBreakpoint('down', 'sm')
  return (
    <>
      <TooltipComponent title={me?.fullName} arrow variant="light">
        <Button
          aria-controls={id}
          className={open ? classes.selected : classes.button}
          onClick={handleMenuOpen}
        >
          <div className={classes.content}>
            <PersonWidget
              title={isSmDown ? '' : me?.fullName}
              user={me?.id}
              titleProps={{ variant: 'button', color: 'primary' }}
            />
          </div>
        </Button>
      </TooltipComponent>
      <Menu
        anchorEl={anchorEl}
        MenuListProps={{ id }}
        open={open}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem className={classes.menuItem} component="a" href="/profile" onClick={handleMenuClose}>
          <ListItemIcon>
            <AccountCircleRoundedIcon color="primary" />
          </ListItemIcon>
          <ListItemText primaryTypographyProps={primaryTypographyProps}>{i18n`Your Profile`}</ListItemText>
        </MenuItem>
        {currentFacility?.crmFacilityId
          ? (
            <Allowed actions="manage_account" features="CRM_INTEGRATION">
              <MenuItem className={classes.menuItem} component="a" href="/account" onClick={handleMenuClose}>
                <ListItemIcon>
                  <BusinessRoundedIcon color="primary" />
                </ListItemIcon>
                <ListItemText primaryTypographyProps={primaryTypographyProps}>{i18n`Account`}</ListItemText>
              </MenuItem>
            </Allowed>
          )
          : null}
        <MenuItem className={classes.menuItem} onClick={handleLogout}>
          <ListItemIcon><PowerSettingsNewRoundedIcon color="primary" /></ListItemIcon>
          <ListItemText primaryTypographyProps={primaryTypographyProps}>{i18n`Logout`}</ListItemText>
        </MenuItem>
      </Menu>
      {loggingOut && shouldShowLogoutConfirmation ? (
        <ConfirmationDialog
          confirmText={i18n`Cancel logout`}
          cancelText={i18n`Logout`}
          destructive={false}
          maxWidth="sm"
          open={loggingOut}
          title={i18n`Are you sure you want to logout?`}
          titleProps={{ variant: 'h3' }}
          onCancel={(_, reason) => {
            if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') doLogout()
          }}
          onConfirm={() => setLoggingOut(false)}
        >
          <T.H5.Lite color="textSecondary" component="p">
            {i18n`Some harvested weights have not been saved to the cloud.`}
            {' '}
            {i18n`Logging out of AROYA will result in the loss of this data.`}
          </T.H5.Lite>
        </ConfirmationDialog>
      ) : null}
    </>
  )
}
AccountButtonComponent.displayName = displayName

AccountButtonComponent.propTypes = {
  currentFacility: PropTypes.shape({
    crmFacilityId: PropTypes.string,
  }),
  haveHarvestingUnsavedStates: PropTypes.bool.isRequired,
  me: PropTypes.shape({
    id: PropTypes.number,
    fullName: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }).isRequired,
  urlObject: PropTypes.shape({
    pathname: PropTypes.string
  }).isRequired,
  doLogout: PropTypes.func.isRequired,
}

const Connected = props => {
  const connectedProps = useConnect(
    'doLogout',
    'selectCurrentFacility',
    'selectHaveHarvestingUnsavedStates',
    'selectMe',
    'selectUrlObject',
  )
  return <AccountButtonComponent {...connectedProps} {...props} />
}
Connected.displayName = `Connected(${AccountButtonComponent.displayName})`

const Memoized = React.memo(Connected)
export default Memoized
